import React, { useEffect, useRef, useState } from "react";
import { useViewportSpy } from "beautiful-react-hooks";
import ListBanner from "./components/ListBanner/ListBanner";
import Breadcrumb from "./components/Breadcrumb/Breadcrumb";
import Dropdown from "./../dropdown";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { buildQuery } from "../../helpers/url";
import { Helmet } from "react-helmet";
import Newsletter from "../Newsletter";
import NewBookCard from "../bookcard/NewBookCard";
import PageDescription from "./components/PageDescription/PageDescription";
import Filters from "./components/Filters";

const dropdownItems = [
    { value: "discount", label: "Akciós termékek elöl" },
    { value: "titlea", label: "Cím szerint növekvő" },
    { value: "titled", label: "Cím szerint csökkenő" },
    { value: "authora", label: "Szerző neve szerint növekvő" },
    { value: "authord", label: "Szerző neve szerint csökkenő" },
    { value: "publisha", label: "Megjelenés szerint növekvő" },
    { value: "publishd", label: "Megjelenés szerint csökkenő" },
    { value: "price", label: "Ár szerint növekvő" },
    { value: "priced", label: "Ár szerint csökkenő" },
];

const urlMapping = {
    'elojegyezheto': 'hamarosan',
}

const getParentCategoryId = (categories, alkategoriaId) => {
    for (const category of categories) {
        if (category.items && category.items.some(sub => sub.id === alkategoriaId)) {
            return category.id;
        }
        if (category.items) {
            const parentId = getParentCategoryId(category.items, alkategoriaId);
            if (parentId) return parentId;
        }
    }
    return null;
};

export const getListerUrlParams = (pathname) => {
    pathname ??= window.location.href;
    pathname = pathname.replace(window.location.origin, "");
    const params = new URLSearchParams(pathname.split("?")[1] || "");
    pathname = pathname.split("?")[0] || "";
    
    const categoryUrlMatch = pathname.match(/\/kategoriak\/([^\/]+)(?:\/([^\/]+))?\/?$/);
    if(categoryUrlMatch) {
        if(categoryUrlMatch[1]) {
            params.append("kategoria", categoryUrlMatch[1]);
        }

        if(categoryUrlMatch[2]) {
            params.append("alkategoria", categoryUrlMatch[2]);
        }
    }

    const marketingUrlMatch = pathname.match(/\/m\/([^\/]+)(?:\/([^\/]+))?\/?$/);
    if (marketingUrlMatch) {
        if (marketingUrlMatch[1])
            params.append("marketing", marketingUrlMatch[1]);
    }

    const seriesUrlMatch = pathname.match(/\/sorozat\/([^\/]+)(?:\/([^\/]+))?\/?$/);
    if(seriesUrlMatch) {
        if(seriesUrlMatch[1])
            params.append("sorozat", seriesUrlMatch[1]);
    }

    if(!categoryUrlMatch && !marketingUrlMatch && !seriesUrlMatch) {
        const urlSlug = pathname.replace(/^\//, "").replace(/\/$/, "").replace("konyvek/", "");
        
        if(['ujdonsag', 'ingyenes', 'elojegyezheto', 'toplista'].includes(urlSlug)) {
            params.append("bongeszde", urlMapping[urlSlug] ?? urlSlug);
        } else if(['ekonyv', 'hangoskonyv', 'csomag'].includes(urlSlug)) {
            params.append("tipus", urlSlug);
        }
    }

    const authorUrlMatch = pathname.match(/\/szerzo\/([^\/]+)(?:\/([^\/]+))?\/?$/);
    if(authorUrlMatch) {
        if(authorUrlMatch[1]) {
            params.append("szerzo", authorUrlMatch[1]);
        }
    }

    return params;
}

export default function Lister(props) {
    const noFilters = props.elem?.dataset?.noFilters === "true";
    const gdpr = props.elem?.dataset?.gdpr;
    let dataType = props.elem?.dataset?.type ? props.elem.dataset.type : props.type;
    let dataDomFilters = props.elem?.dataset?.filters ? props.elem.dataset.filters : props.filters;
    let dataBreadcrumbParams = props.elem?.dataset?.breadcrumb
        ? props.elem.dataset.breadcrumb
        : props.breadcrumb;
    let loggedin = props.elem?.dataset?.loggedin
        ? props.elem.dataset.loggedin !== "false"
        : props.loggedin;

    let firstRun = useRef(true);
    let listerRef = useRef();
    let listLoaderRef = useRef();
    let booksPerPage = useRef(20);
    let isLoaderVisible = useViewportSpy(listLoaderRef, { threshold: 0 });

    let [config, setConfig] = useState({
        filters: {},
        breadcrumb: { params: [] },
    });
    let [data, setData] = useState({});
    let [filters, setFilters] = useState([]);
    let [loaderActive, setLoaderActive] = useState(true);
    let [loaderElemVisible, setLoaderElemVisible] = useState(true);
    let [loaderIndex, setLoaderIndex] = useState(1);
    let [loaderPage, setLoaderPage] = useState(1);
    let [selectedFilterItems, setSelectedFilterItems] = useState({});
    let [defaultOpenFilters, setDefaultOpenFilters] = useState({});
    let [skipSelectedFilterItemsCache, setSkipSelectedFilterItemsCache] = useState({});
    let [selectedFilterItemsCache, setSelectedFilterItemsCache] = useState({});
    let [skipSelectedSortCache, setSkipSelectedSortCache] = useState(false);
    let [selectedSortCache, setSelectedSortCache] = useState("");
    let [selectedSort, setSelectedSort] = useState("");
    let [isAllLoaded, setIsAllLoaded] = useState(false);
    let [currentUrl, setCurrentUrl] = useState("");
    let [url, setUrl] = useState(window.location.href);

    useEffect(() => {
        let domFilters = sortFilter(JSON.parse(dataDomFilters));
        let breadcrumbParams = JSON.parse(dataBreadcrumbParams);
        let config = {
            filters: domFilters,
            breadcrumb: {
                params: breadcrumbParams,
            },
            query: {},
        };

        setConfig(config);
    }, []);

    // Initial query loading or API call
    useEffect(() => {
        if (firstRun.current) return;

        // Parsing URL params, sorry IE not for you :(
        const params = getListerUrlParams(url);
        let selectedItems = [];
        let sortBy = "";

        if(params.get("kategoria")) {
            setDefaultOpenFilters(prev => ({
                ...prev,
                ['kategoria#'+params.get("kategoria")]: true,
            }));
        }

        // Filtering params for allowed
        Array.from(params).forEach((item) => {
            let [key, value] = item;

            // The key is a filter param so we save for later
            selectedItems = [...selectedItems, value + '#' + key];

            // Save sorting key
            if (key === "sort") {
                sortBy = value;
            }
        });

        // Populating the selected
        window.Dibook.book
            .fetchBooks(params.toString())
            .then((responseData) => {
                if (responseData.status < 300) {
                    setData(responseData);

                    // Loader
                    if (responseData.content.data.total <= booksPerPage.current) {
                        setLoaderActive(false);
                    }

                    // Setting selected filter items and sort mode
                    // while skipping dependency trigger once
                    setSelectedSort(sortBy);
                    setIsAllLoaded(responseData.content.data.items.length === 0);

                    if(responseData.content.data.seo?.url) {
                        window.history.replaceState({ path: responseData.content.data.seo.url }, "", responseData.content.data.seo.url);
                        setUrl(responseData.content.data.seo.url);
                    }
                }
            });

        //csomagajánlat, akciós, előjegyezhetők, újdonságok és idegen nyelvű
        const filterParams = new URLSearchParams();

        for (const entry of params) {
            if(entry[0] === "tipus" && entry[1] === "csomag") {
                filterParams.append(entry[0], entry[1]);
            } else if(entry[0] === "bongeszde" && ['hamarosan', 'ujdonsag', 'akcios'].includes(entry[1])) {
                filterParams.append(entry[0], entry[1]);
            } else if(entry[0] === "kategoria" && ['idegen-nyelvu-konyvek'].includes(entry[1])) {
                filterParams.append(entry[0], entry[1]);
            } else if(['kiado', 'szerzo'].includes(entry[0])) {
                filterParams.append(entry[0], entry[1]);
            } 
        }    

        window.Dibook.book.fetchFilters(filterParams.toString())
            .then((responseData) => {
                if (responseData.status < 300) {
                    // Now we have to populate local state
                    let newSelectedFilterItems = {};

                    const getSelectedFilters = (filterCat, newSelectedFilterItems) => {
                        if(!newSelectedFilterItems[filterCat.param])
                            newSelectedFilterItems[filterCat.param] = [];
                        
                        if(selectedItems.includes(filterCat.id + '#' + filterCat.param)) {
                            newSelectedFilterItems[filterCat.param].push(filterCat);
                        }

                        if (filterCat.items) {
                            filterCat.items.forEach((item) => {
                                getSelectedFilters(item, newSelectedFilterItems);
                            });
                        }
                    }

                    responseData.content.forEach((filterCat) => {
                        getSelectedFilters(filterCat, newSelectedFilterItems);
                    });

                    // If we have kategoria param, we need to open the parent kategoria filter
                    const kategoriaId = params.get("kategoria");
                    if (kategoriaId) {
                        setDefaultOpenFilters(prev => ({
                            ...prev,
                            ['kategoria#' + kategoriaId]: true,
                        }));
                    }


                    // If we have alkategoria param, we need to open the parent kategoria filter
                    const alkategoriaId = params.get("alkategoria");
                    if (alkategoriaId) {
                        const parentCategoryId = getParentCategoryId(responseData.content, alkategoriaId);

                        if (parentCategoryId) {
                            setDefaultOpenFilters(prev => ({
                                ...prev,
                                ['kategoria#' + parentCategoryId]: true,
                            }));
                        }
                    }

                    // Setting selected filter items and sort mode
                    // while skipping dependency trigger once
                    setFilters(removeEmptyFilters(responseData.content));
                    setSkipSelectedFilterItemsCache(true);
                    setSelectedFilterItemsCache(newSelectedFilterItems);
                    setSelectedFilterItems(newSelectedFilterItems);
                }
            });
    }, [config]);

    // On filter item selection change we generate query
    useEffect(() => {
        if (firstRun.current) return;

        if (skipSelectedFilterItemsCache) {
            setSkipSelectedFilterItemsCache(false);
            return;
        }

        // API call
        let fetchQuery = buildQuery(
            selectedFilterItemsQueryPrepare(selectedFilterItemsCache),
            { sort: selectedSort }
        );

        let host = window.location.origin;
        let newUrl = host + "/konyvek?" + fetchQuery;
        window.history.pushState({ path: newUrl }, "", newUrl);

        if (currentUrl !== newUrl) {
            setCurrentUrl(newUrl);
        }

        window.Dibook.book.fetchBooks(fetchQuery).then((responseData) => {
            if (responseData.status < 300) {
                setData(responseData);
                setSelectedFilterItems(selectedFilterItemsCache);
                setIsAllLoaded(responseData.content.data.items.length === 0);

                setLoaderActive(true);
                setLoaderElemVisible(true);
                setLoaderPage(1);
                setLoaderIndex(1);

                // Loader
                if (responseData.content.data.total <= booksPerPage.current) {
                    setLoaderActive(false);
                }

                if(responseData.content.data.seo?.url) {
                    window.history.replaceState({ path: responseData.content.data.seo.url }, "", responseData.content.data.seo.url);
                    setUrl(responseData.content.data.seo.url);
                }
            }
        });

        // window.Dibook.book.fetchFilters(fetchQuery).then((responseData) => {
        //     if (responseData.status < 300) {
        //         setFilters(removeEmptyFilters(responseData.content));
        //     }
        // });
    }, [selectedFilterItemsCache]);

    // On sorting change we generate query
    useEffect(() => {
        if (firstRun.current) return;

        if (skipSelectedSortCache) {
            setSkipSelectedSortCache(false);
            return;
        }

        let fetchQuery = buildQuery(
            selectedFilterItemsQueryPrepare(selectedFilterItemsCache),
            { sort: selectedSortCache }
        );

        window.Dibook.book.fetchBooks(fetchQuery).then((responseData) => {
            if (responseData.status < 300) {
                setData(responseData);
                setSelectedSort(selectedSortCache);
                setIsAllLoaded(responseData.content.data.items.length === 0);

                setLoaderActive(true);
                setLoaderElemVisible(true);
                setLoaderPage(1);
                setLoaderIndex(1);

                // Loader
                if (responseData.content.data.total <= booksPerPage.current) {
                    setLoaderActive(false);
                }
            }
        });
    }, [selectedSortCache]);

    // If book loader is visible
    useEffect(() => {
        if (firstRun.current || !isLoaderVisible) return;
        if (!get(data, "content.data.items")) return;

        // API call
        let fetchQuery = buildQuery(
            selectedFilterItemsQueryPrepare(selectedFilterItems),
            { page: loaderPage + 1 },
            { sort: selectedSort }
        );

        window.Dibook.book.fetchBooks(fetchQuery).then((responseData) => {
            if (responseData.status < 300) {
                // After 3 loads we hide autoloader (don't ask)
                if (loaderIndex + 1 <= 2) setLoaderElemVisible(false);

                // If there are no more books
                if (responseData.content.data.total <= (loaderPage + 1) * booksPerPage.current) {
                    setLoaderActive(false);
                }

                setLoaderIndex(loaderIndex + 1);
                setLoaderPage(loaderPage + 1);
                setData(mergeDataItems(data, responseData));
                setIsAllLoaded(responseData.content.data.items.length === 0);
            }
        });
    }, [isLoaderVisible]);

    // First run handler
    useEffect(() => {
        if (firstRun.current) firstRun.current = false;
    }, []);

    let helmetMetaTitle = get(data, "content.data.seo.meta_title");
    let helmetMetaDescription = get(data, "content.data.seo.meta_description");

    let seoTitle = get(data, "content.data.seo.title");
    let seoDescription = get(data, "content.data.seo.description");
    let seoImage = get(data, "content.data.seo.image");

    let defaultBreadcrumbTitle =
        get(data, "content.data.total") === 0 || get(data, "content.data.total") > 0
            ? ""
            : "Betöltés alatt...";

    let [searchInputs, setSearchInputs] = useState({});

    useEffect(() => {
        let searchInputDefault = {};
        filters.map(
            (filter) => (searchInputDefault[filter.param] = "")
        );
        setSearchInputs(searchInputDefault);
    }, [filters]);

    function removeEmptyFiltersArrays(filters) {
        const newFilters = { ...filters };
        Object.keys(newFilters).forEach((key) => {
            if (newFilters[key].length === 0) delete newFilters[key];
        });
        return newFilters;
    }

    const results = get(data, "content.data.items", null);

    useEffect(() => {
        window.scrollTo(0, 0, "smooth");
    }, [currentUrl]);

    const firstItemCount = Math.floor(listerRef?.current?.clientWidth / 265) * 2

    return (
        <>
            <Helmet>
                {helmetMetaTitle ? (
                    <title>{helmetMetaTitle}</title>
                ) : (
                    <title>Digitális könyv vásárlás - DiBook</title>
                )}
                {helmetMetaDescription ? (
                    <meta name="description" content={helmetMetaDescription} />
                ) : (
                    <meta name="description" content="digitális magazin, olvasás, újság, e-paper" />
                )}
                {helmetMetaTitle ? (
                    <meta property="og:title" content={helmetMetaTitle} />
                ) : (
                    <meta property="og:title" content="Digitális könyv vásárlás  - DiBook.hu" />
                )}
                {helmetMetaDescription ? (
                    <meta property="og:description" content={helmetMetaDescription} />
                ) : (
                    <meta
                        property="og:description"
                        content="digitális magazin, olvasás, újság, e-paper"
                    />
                )}
            </Helmet>

            <div>
                <div
                    className="list-main flex-nowrap justify-content-start pl-0 mt-2"
                >
                    {filters.length > 0 ? (
                        <Filters
                            filters={filters}
                            config={config}
                            searchInputs={searchInputs}
                            selectedFilterItems={selectedFilterItems}
                            defaultOpenFilters={defaultOpenFilters}
                            selectedFilterItemsNotEmpty={selectedFilterItemsNotEmpty}
                            handleFilterItemSelect={handleFilterItemSelect}
                            handleFilterItemsReset={handleFilterItemsReset}
                            setSearchInputs={setSearchInputs}
                        />
                    ) : (
                        !noFilters && <div className="list-filter-col col-lg-3 invisible" />
                    )}
                    <div className="search-results-section px-4">
                        <div ref={listerRef}>
                            <div>
                                <div className="list-main mb-3">
                                    <div className="d-flex flex-column">
                                        <Breadcrumb
                                            title={get(data, "content.data.title", defaultBreadcrumbTitle)}
                                            total={get(data, "content.data.total", 0)}
                                            params={config.breadcrumb.params}
                                            selected={selectedFilterItems}
                                        />
                                        {get(data, "content.data.policy") && (
                                            <div className="filter-links mt-2">
                                                <a
                                                    href={data.content.data.policy}
                                                    className="filter-links__item"
                                                    style={{ fontWeight: 500, fontSize: "1.25rem" }}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <div className="filter-links__item-text">
                                                        Akciós feltételek és szabályzat
                                                    </div>
                                                </a>
                                            </div>
                                        )}

                                        <PageDescription dataType={dataType} url={url} />
                                    </div>
                                    <div className="list-order">
                                        <div className="list-order-select">
                                            <Dropdown
                                                items={dropdownItems}
                                                placeholder={"Sorrendezés"}
                                                selected={selectedSort}
                                                onSelect={handleSortDropdownSelect}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {selectedFilterItemsNotEmpty(selectedFilterItems) && (
                                    <div className="list-order-btns px-1 mb-3">
                                        <button
                                            className="clear-filters-btn"
                                            onClick={handleFilterItemsReset}
                                        >
                                            Szűrők törlése
                                        </button>
                                    </div>
                                )}
                                <div className="mb-2 px-2 d-flex align-items-center justify-content-end">
                                    {Object.keys(selectedFilterItems || {}).length > 0 && (
                                        <div
                                            className="d-md-flex d-none align-items-center"
                                            style={{ gap: 10, flex: 1 }}
                                        >
                                            {Object.entries(removeEmptyFiltersArrays(selectedFilterItems)).map((entry, entryKey) =>
                                                entry[1].map((item, itemKey) => (
                                                    <button
                                                        key={item.id}
                                                        className="remove-filter-btn"
                                                        onClick={() =>
                                                            handleFilterItemSelect({
                                                                id: item.id,
                                                                param: entry[0],
                                                                title: item.title,
                                                                multi: true,
                                                            })
                                                        }
                                                    >
                                                        {entryKey == Object.keys(removeEmptyFiltersArrays(selectedFilterItems)).length - 1 && itemKey == entry[1].length - 1 ? (
                                                            <h1>{item.title}</h1>
                                                        ) : (
                                                            <span>{item.title}</span>
                                                        )}
                                                        <img
                                                            src="../../../images/icon/close-icon.svg"
                                                            alt="close-icon"
                                                        />
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                    <div className="list-order-btns">
                                        <div
                                            className="list-filters-toggle"
                                            id="list-filters-toggle"
                                        />
                                    </div>
                                </div>

                                {results !== null &&
                                    (results.length > 0 ? (
                                        <div className="books-results-container align-items-end">
                                            {(results.length >= 20 ? results.slice(0, firstItemCount ?? 10) : results).map((item) => (
                                                <div
                                                    key={item.bookid}
                                                    className="bookcard-container"
                                                >
                                                    <NewBookCard
                                                        loggedin={loggedin}
                                                        {...item}
                                                        layout="table"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="m-0 font-weight-bold">
                                            Jelenleg nincs találat a szűrési feltételeknek.
                                        </p>
                                    ))}

                                <ListBanner
                                    data={get(data, "content.data.ads.scroll_banner", {})}
                                />
                                {results !== null && <div className="py-4" style={{ width: "100%" }}>
                                    <Newsletter gdpr={gdpr} />
                                </div>}

                                {results !== null && results.length >= 20 && (
                                    <div className="books-results-container align-items-end">
                                        {results.slice(firstItemCount ?? 10).map((item) => (
                                            <div key={item.bookid} className="bookcard-container">
                                                <NewBookCard
                                                    loggedin={loggedin}
                                                    {...item}
                                                    layout="table"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={"loaders" + (!loaderActive ? " loaders--inactive" : "")}>
                            <div
                                className={
                                    "loaders__elem" +
                                    (!loaderElemVisible ? " loaders__elem--hidden" : "")
                                }
                                ref={listLoaderRef}
                            >
                                {typeof data != "undefined" && !isAllLoaded && (
                                    <div className="loader"></div>
                                )}
                            </div>
                            <div
                                className={
                                    "loaders__button" +
                                    (loaderElemVisible ? " loaders__button--hidden" : "")
                                }
                            >
                                {typeof data != "undefined" && !isAllLoaded && (results ? results.length : 0) != get(data, "content.data.total", 0) && (
                                    <button className="btn" onClick={handleLoadMoreButtonClick}>
                                        Még több betöltése
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {(seoTitle || seoDescription) && (
                <section className={`seo-section ${!seoImage ? 'd-lg-flex' : ''}`}>
                    {!noFilters && <div className="list-filter-col col-lg-3 invisible" />}
                    <div
                        className="container"
                        style={
                            props.elem.className.includes("grid-view") ? { display: "block" } : {}
                        }
                    >
                        {!!seoImage && (
                            <div className="seo-image">
                                <img src={seoImage} loading="lazy" alt="seo-image" />
                            </div>
                        )}

                        <div>
                            {seoTitle && (<h2 className="title">{seoTitle}</h2>)}
                            {seoDescription && (
                                <div className="content overflow open">
                                    <div className="content--wrapper">
                                        <div
                                            className="seo-content"
                                            dangerouslySetInnerHTML={{
                                                __html: seoDescription,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}
        </>
    );

    // Are these selected items in filters?
    function selectedFilterItemsNotEmpty(selectedFilterItems) {
        for (let key in selectedFilterItems) {
            if (selectedFilterItems[key].length > 0) return true;
        }

        return false;
    }

    function handleLoadMoreButtonClick() {
        setLoaderIndex(1);
        setLoaderElemVisible(true);
    }

    function removeEmptyFilters(filters) {
        let filteredData = cloneDeep(filters);

        if (filters) {
            filteredData = filters.filter(
                (filter) => (filter.items && filter.items.length > 0) || (filter?.count != null && filter.count > 0)
            );
        }

        return filteredData;
    }

    function sortFilter(filters) {
        let filterObject = {};

        filters.forEach((filter) => {
            filterObject[filter.param] = filter;
        });

        return filterObject;
    }

    function selectedFilterItemsQueryPrepare(selectedFilterItems) {
        let itemsArray = {};

        for (let key in selectedFilterItems) {
            itemsArray[key] = [];
            selectedFilterItems[key].forEach((item) => {
                itemsArray[key].push(item.id);
            });
        }

        return itemsArray;
    }

    function handleSortDropdownSelect(label, value) {
        setSelectedSortCache(value);
    }

    function handleFilterItemsReset() {
        setSelectedFilterItemsCache({});
        setSelectedSortCache("");
    }

    function mergeDataItems(data, newData) {
        let mergedData = cloneDeep(data);
        let mergedDataItems = get(mergedData, "content.data.items");
        let newDataItems = get(newData, "content.data.items");

        if (!mergedDataItems || !newDataItems) return;

        mergedData.content.data.items = mergedDataItems.concat(newDataItems);

        return mergedData;
    }

    function handleFilterItemSelect(selection) {
        let items = cloneDeep(selectedFilterItems);

        const param = selection.param.split("#")[0];
        if (!items[param]) {
            items[param] = [];
        }

        // If exists removing if not adding
        if (getSelectedFilterItemById(items, selection.id)) {
            setSelectedFilterItemsCache(removeItemById(items, selection.id));
        } else {
            if (!selection.multi) {
                items[param] = [];
            }

            items[param].push({
                title: selection.title,
                id: selection.id,
                old_id: selection.old_id,
            });

            setSelectedFilterItemsCache(items);

            // Custom event ping
            if (param === "kategoria") {
                window.Dibook.ping.categorySelection({
                    id: selection.id,
                    name: selection.title,
                    _location: "lister",
                });
            }
        }
    }

    function getSelectedFilterItemById(items, id) {
        let item = null;

        for (let key in items) {
            for (let iterator of items[key]) {
                if (iterator.id === id) return iterator;
            }
        }

        return item;
    }

    // Keeps the local selectedFilterItems in sync with the fetched data
    // After data fetch selected items remain in local state, we need to prune out those
    // who are not in newly fetched filter data
    function syncSelectedFilterItemsWithData(dataFilters, selectedFilterItems) {
        // Inmutable
        let filteredSelectedFilterItems = cloneDeep(selectedFilterItems);

        for (let selectedFilterItemsKey in filteredSelectedFilterItems) {
            // Array is empty, we dont care
            if (filteredSelectedFilterItems[selectedFilterItemsKey].length <= 0) continue;

            // Iterating trough selected items in category
            filteredSelectedFilterItems[selectedFilterItemsKey].forEach(
                (selectedFilterItem, selectedFilterItemIndex) => {
                    // Looking up if category id exists in data id
                    dataFilters.forEach((dataFilter) => {
                        // Items we need to check are in this data category
                        if (dataFilter.param === selectedFilterItemsKey) {
                            if (dataFilter.items && dataFilter.items.length > 0) {
                                // Is the filter item id in data item list?
                                if (
                                    findInArrayById(dataFilter.items, selectedFilterItem.id) ===
                                    null
                                ) {
                                    filteredSelectedFilterItems[selectedFilterItemsKey].splice(
                                        selectedFilterItemIndex,
                                        1
                                    );
                                }
                            }
                        }
                    });
                }
            );
        }

        return filteredSelectedFilterItems;
    }

    function findInArrayById(array, id) {
        let find = null;

        array.forEach((item, index) => {
            if (item.id === id) find = index;
        });

        return find;
    }

    function removeItemById(items, id) {
        let filteredItems = {};
        for (let key in items) filteredItems[key] = items[key].filter((item) => item.id !== id);
        return filteredItems;
    }
}
